import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeSlash, LockKey } from "@phosphor-icons/react";
import { useConnectAuth } from "./ConnectAuthContext";
import { toast } from "sonner";

const ConnectLoginPage = () => {
  const { login, changePassword } = useConnectAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [loginData, setLoginData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.must_change_password && data.role === "partner") {
        setMustChangePassword(true);
        setLoginData(data);
      } else if (data.role === "admin") {
        navigate("/connect/admin");
      } else {
        navigate("/connect/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (newPassword !== confirmPassword) { setError("Les mots de passe ne correspondent pas"); return; }
    setChangingPw(true);
    try {
      await changePassword(newPassword);
      toast.success("Mot de passe créé avec succès !");
      if (loginData?.role === "admin") navigate("/connect/admin");
      else navigate("/connect/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally { setChangingPw(false); }
  };

  // Password change screen
  if (mustChangePassword) {
    return (
      <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="change-password-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="text-center mb-10">
            <span className="font-heading text-2xl tracking-[0.35em] text-[#1A1A1A] uppercase">Altura</span>
            <h1 className="font-heading text-2xl text-[#1A1A1A] mt-4 mb-2">Créez votre mot de passe</h1>
            <p className="text-[#1A1A1A]/60 text-sm font-light">Bienvenue ! Choisissez un mot de passe personnel pour sécuriser votre compte.</p>
          </div>
          <div className="bg-white border border-[#1A1A1A]/10 p-8" data-testid="change-password-form-container">
            <div className="flex items-center gap-3 mb-6 p-4 bg-blue-50 border border-blue-200">
              <LockKey size={20} className="text-blue-600 flex-shrink-0" />
              <p className="text-blue-800 text-xs">Pour votre sécurité, veuillez créer un mot de passe personnel. Il remplacera le mot de passe temporaire.</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-5">
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Nouveau mot de passe</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8}
                  className="input-luxury w-full bg-transparent" placeholder="8 caractères minimum" data-testid="new-password-input" />
              </div>
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Confirmer le mot de passe</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  className="input-luxury w-full bg-transparent" placeholder="Confirmez votre mot de passe" data-testid="confirm-password-input" />
              </div>
              {error && <p className="text-red-600 text-sm" data-testid="change-pw-error">{error}</p>}
              <button type="submit" disabled={changingPw} className="btn-dark w-full flex items-center justify-center gap-3" data-testid="change-pw-submit">
                {changingPw ? "Enregistrement..." : "Créer mon mot de passe"}
                <ArrowRight size={16} weight="bold" />
              </button>
            </form>
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="connect-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="inline-block mb-6">
            <span className="font-heading text-2xl tracking-[0.35em] text-[#1A1A1A] uppercase">Altura</span>
          </Link>
          <h1 className="font-heading text-3xl text-[#1A1A1A] mb-2">Connect</h1>
          <p className="text-[#1A1A1A]/60 text-sm font-light">Portail partenaires — Espace réservé</p>
        </div>

        <div className="bg-white border border-[#1A1A1A]/10 p-8" data-testid="login-form-container">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="input-luxury w-full bg-transparent" placeholder="votre@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="input-luxury w-full bg-transparent pr-10" placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && <p className="text-red-600 text-sm" data-testid="login-error">{error}</p>}

            <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3" data-testid="login-submit-btn">
              {loading ? "Connexion..." : "Se connecter"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>

        <div className="text-center mt-6 space-y-3">
          <Link to="/connect/forgot-password" className="text-[#1A1A1A]/60 text-sm hover:text-[#1A1A1A] transition-colors block" data-testid="forgot-password-link">
            Mot de passe oublié ?
          </Link>
          <Link to="/connect/apply" className="text-[#1A1A1A]/60 text-sm hover:text-[#1A1A1A] transition-colors block" data-testid="apply-link">
            Devenir partenaire — Demander l{"'"}accès
          </Link>
        </div>
      </motion.div>
    </main>
  );
};

export default ConnectLoginPage;
